import Head from 'next/head';
import { useRouter } from 'next/router';
import { ReactElement } from 'react';
import TrustPilotBanner from '../../components/common/blocks/TrustPilotBanner';
import { ICookiesPopupProps } from '../../components/common/CookiesPopUp';
import { IFooterProps } from '../../components/common/layout/Footer';
import Layout from '../../components/common/layout/Layout';
import { INavBarProps } from '../../components/common/layout/NavBar';
import Contact, { IContactProps } from '../../components/contact-us/Contact';
import { PageJSONLDSchema } from '../../models/app.models';
import {
  IContactMethod,
  IContactUsForm,
  IContactUsPage,
  ICustomerReviewsMeta,
  ILayoutPageProps,
} from '../../models/strapi.models';
import { fetchStrapi } from '../../services/api.service';
import { getRoutes } from '../../shared/useBuild';
import {
  buildApiKey,
  getCommonTranslations,
  populateCookiesPopUpProps,
  populateFooterProps,
  populateNavBarProps,
} from '../../shared/useCommon';
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
} from '../../shared/useJSONLDSchema';
import {
  flatStrapiCollection,
  getAlternateLinks,
  getCanonicalLink,
  getContentLanguageCode,
  getTranslation,
} from '../../shared/useUtils';
import { NextPageWithLayout } from '../_app';

interface IContactUsPageProps extends ILayoutPageProps {
  formSuccessMessage: string;
  formErrorMessage: string;
  contactMethods: IContactMethod[];
  contactUsForm: IContactUsForm;
  alternativeLinks: { hrefLang: string; href: string }[];
  canonicalLink: string;
}

const ContactUsPage: NextPageWithLayout<IContactUsPageProps> = (
  props: IContactUsPageProps
) => {
  const router = useRouter();

  const contactProps: IContactProps = {
    contactMethods: props.contactMethods.map((x) => ({
      type: x.type,
      label: getTranslation(x.labelTranslations, router.locale || 'en')
        .text as string,
      value:
        x.valueTranslations && x.valueTranslations.length > 0
          ? (getTranslation(x.valueTranslations, router.locale || 'en')
              .text as string)
          : undefined,
    })),
    formLabels: {
      title: props.contactUsForm.title,
      subTitle: props.contactUsForm.subTitle,
      name: props.contactUsForm.nameLabel,
      email: props.contactUsForm.emailLabel,
      message: props.contactUsForm.messageLabel,
      submit: props.contactUsForm.submitLabel,
      submitSuccessMessage: props.formSuccessMessage,
      submitErrorMessage: props.formErrorMessage,
    },
    slot: <TrustPilotBanner locale={router.locale || 'en'} />,
  };

  return (
    <div className="page-container">
      <Head>
        <title>{props.meta.title}</title>
        <meta name="description" content={props.meta.description} />
        <meta name="keywords" content={props.meta.keywords} />
        <link rel="canonical" href={props.canonicalLink} />
        {props.alternativeLinks.map((x, i) => (
          <link key={i} rel="alternate" hrefLang={x.hrefLang} href={x.href} />
        ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(props.pageSchema.nodes),
          }}
        />
      </Head>

      <div className="page-content-section is-narrower">
        <div className="page-content-block">
          <Contact {...contactProps} />
        </div>
      </div>
    </div>
  );
};

ContactUsPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <Layout
      locale={page.props.locale}
      navBarProps={page.props.navBarProps}
      cookiesPopUpProps={page.props.cookiesPopUpProps}
      footerProps={page.props.footerProps}
      contentLanguageCode={getContentLanguageCode(page.props.locale)}>
      {page}
    </Layout>
  );
};

export async function getStaticProps(
  context: any
): Promise<{ props: IContactUsPageProps }> {
  const appRoutes = await getRoutes();

  const pageUrlParams = new URLSearchParams({
    populate: '*',
  });

  const contactMethodsUrlParams = new URLSearchParams({
    'populate[0]': 'labelTranslations',
    'populate[1]': 'valueTranslations',
  });

  const page = (
    await fetchStrapi<any>(`/contact-us-page`, pageUrlParams, buildApiKey)
  ).data.attributes as IContactUsPage;

  const contactMethods = flatStrapiCollection(
    await fetchStrapi<any>(
      `/contact-methods`,
      contactMethodsUrlParams,
      buildApiKey
    )
  ) as IContactMethod[];

  const navBarProps: INavBarProps = await populateNavBarProps(context.locale);
  const cookiesPopUpProps: ICookiesPopupProps = await populateCookiesPopUpProps(
    context.locale
  );
  const footerProps: IFooterProps = await populateFooterProps(context.locale);
  const commonTranslations = await getCommonTranslations();

  const websiteSchema = generateWebsiteSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string
  );
  const customerReviewsMeta = (
    await fetchStrapi<any>(`/customer-reviews-meta`, undefined, buildApiKey)
  ).data.attributes as ICustomerReviewsMeta;
  const organizationSchema = await generateOrganizationSchema(
    `https://www.igerent.com${
      context.locale !== 'en' ? `/${context.locale}` : ''
    }`,
    getTranslation(commonTranslations.organizationDescription, context.locale)
      .text as string,
    customerReviewsMeta
  );
  const pageSchema: PageJSONLDSchema = {
    nodes: [...websiteSchema, ...organizationSchema],
  };

  const meta = getTranslation(page.meta, context.locale);
  const alternativeLinks = getAlternateLinks('/contact-us', appRoutes);
  const canonicalLink = getCanonicalLink(context.locale || 'en', meta.slug);

  const props: IContactUsPageProps = {
    // Layout
    pageSchema,
    navBarProps,
    cookiesPopUpProps,
    footerProps,
    // Page
    locale: context.locale,
    meta,
    alternativeLinks,
    canonicalLink,
    formSuccessMessage: getTranslation(
      page.formSuccessMessage,
      context.locale || 'en'
    ).text as string,
    formErrorMessage: getTranslation(
      page.formErrorMessage,
      context.locale || 'en'
    ).text as string,
    contactMethods,
    contactUsForm: getTranslation(page.contactUsForm, context.locale || 'en'),
  };

  return {
    props,
  };
}

export default ContactUsPage;
